import React, { useEffect, useState } from 'react';
import api from '../lib/api';

/**
 * BuildBadge — surfaces /api/status in the top bar.
 *
 * Polls every 60s. Renders a compact "BUILD <sha> · <env>" chip and a
 * detailed tooltip on hover (mongo ping, uptime, SSL days remaining).
 * Color encodes overall status:
 *   • cyan-sys  → ok (mongo connected, SSL > 14d remaining if prod)
 *   • hold      → degraded (SSL < 14d, mongo slow)
 *   • sell      → down (mongo disconnected)
 */
export default function BuildBadge() {
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get('/status');
        if (!cancelled) setS(data);
      } catch {
        if (!cancelled) setS({ status: 'unreachable' });
      }
    };
    tick();
    const iv = setInterval(tick, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  if (!s) return null;

  const sslOk = s.ssl == null || s.ssl.ok;
  const overall =
    s.status === 'ok' && sslOk ? 'ok'
    : s.status === 'unreachable' ? 'down'
    : 'degraded';

  const color =
    overall === 'ok'   ? 'text-cyan-sys border-cyan-sys'
    : overall === 'down' ? 'text-sell border-sell animate-pulse'
    : 'text-hold border-hold';

  const dot =
    overall === 'ok'   ? 'bg-buy'
    : overall === 'down' ? 'bg-sell'
    : 'bg-hold';

  const sha = (s.version || 'dev').slice(0, 7);
  const env = (s.environment || '').toUpperCase();
  const uptimeH = s.uptime_seconds ? (s.uptime_seconds / 3600).toFixed(1) : '–';

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid="build-badge"
    >
      <button
        type="button"
        className={`flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 border ${color}`}
      >
        <span className={`inline-block w-1.5 h-1.5 rounded-full ${dot}`} />
        <span>BUILD {sha}</span>
        {env && <span className="text-[var(--txt-secondary)]">· {env}</span>}
      </button>

      {open && (
        <div
          data-testid="build-badge-tooltip"
          className="absolute right-0 mt-1 z-50 w-[260px] border border-[var(--border-default)] bg-[var(--bg-panel)] p-3 text-[10px] font-mono tracking-wider"
        >
          <div className="text-cyan-sys uppercase mb-2">// production status</div>

          <Row k="status"      v={s.status} ok={s.status === 'ok'} />
          <Row k="env"         v={s.environment} />
          <Row k="version"     v={sha} />
          {s.build_time && <Row k="built"   v={s.build_time.replace('T', ' ').slice(0, 16) + 'Z'} />}
          <Row k="uptime"      v={`${uptimeH}h`} />

          <div className="mt-2 pt-2 border-t border-[var(--border-default)]">
            <Row
              k="mongo"
              v={s.mongo?.connected ? `${s.mongo.latency_ms}ms` : 'down'}
              ok={s.mongo?.connected}
            />
            <Row k="db" v={s.mongo?.db || '—'} />
          </div>

          {s.ssl && (
            <div className="mt-2 pt-2 border-t border-[var(--border-default)]">
              <Row
                k="ssl"
                v={s.ssl.days_until_expiry != null ? `${s.ssl.days_until_expiry}d left` : 'unknown'}
                ok={s.ssl.ok}
              />
              <Row k="domain" v={s.ssl.domain || '—'} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ k, v, ok }) {
  const color =
    ok === true  ? 'text-buy'
    : ok === false ? 'text-sell'
    : 'text-[var(--txt-primary)]';
  return (
    <div className="flex justify-between gap-3 leading-5">
      <span className="text-[var(--txt-secondary)] uppercase">{k}</span>
      <span className={`tabular ${color}`}>{String(v)}</span>
    </div>
  );
}
